import type { Shape, Stroke, TextAnnotation } from "../canvas-kit/drawing/types.js";

/**
 * Whiteboard snapshot — the JSON payload stored in `board.snapshot_json`
 * for rows with `kind='whiteboard'`. Intentionally minimal in the MVP:
 *
 *   - background mode (light / dark)
 *   - optional dot grid (toggle + size + snap behaviour)
 *   - flat lists of drawing primitives reused from `canvas-kit/drawing`
 *     (strokes are uniform-width; any `widths` array on legacy snapshots from
 *     the earlier pressure-stylus experiment is ignored on load)
 *   - sticky notes (post-it style, P1)
 *
 * Future-compatible extension points (widgets, frames, viewport presets,
 * …) get added as additional optional fields — never re-shape existing
 * ones.
 */
export type WhiteboardBg = "light" | "dark";

export interface WhiteboardGrid {
  enabled: boolean;
  /** Spacing of the dot grid in board coordinates. */
  size: number;
  /** When true, freshly placed elements snap to the closest grid intersection. */
  snap: boolean;
}

export type StickyColorId = "yellow" | "pink" | "blue" | "green" | "violet";

export interface StickyNote {
  id: string;
  /** Top-left in board coordinates. */
  x: number;
  y: number;
  /** Card size in board coordinates. */
  w: number;
  h: number;
  /** Free-text body. Multi-line. */
  value: string;
  /** Visual theme — text & paper color come from the preset. */
  color: StickyColorId;
  /** Font size in board units, default 26. */
  fontSize: number;
}

/**
 * Entity widgets (P2). Snapshot-attached cards that reference real domain
 * objects (e.g. players) or carry self-contained drill copy. Player widgets
 * cache `name/position/shirt` at placement time so they keep rendering even
 * if the source row is renamed or soft-deleted.
 */
export type CardAccent = "lime" | "amber" | "rose" | "sky";

export interface PlayerWidget {
  kind: "player";
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  playerId: string;
  name: string;
  position: string | null;
  shirt: number | null;
}

export interface CardWidget {
  kind: "card";
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  title: string;
  body: string;
  accent: CardAccent;
}

export type Widget = PlayerWidget | CardWidget;

export interface WhiteboardSnapshot {
  /** Schema marker — bump if a non-additive change is ever needed. */
  version: 1;
  bg: WhiteboardBg;
  grid: WhiteboardGrid;
  strokes: Stroke[];
  shapes: Shape[];
  texts: TextAnnotation[];
  /** Sticky notes — added in P1, optional in legacy snapshots. */
  stickies: StickyNote[];
  /** Entity widgets — added in P2, optional in legacy snapshots. */
  widgets: Widget[];
  /**
   * Last persisted pan/zoom (P3). When present, the editor restores this
   * viewport on open so the user picks up exactly where they left off
   * instead of being teleported back to centre. Optional — legacy snapshots
   * without a viewport fall back to the default pinch-zoom initial.
   */
  viewport?: { scale: number; x: number; y: number };
  /** Optional cover image (data URL) for the list-page thumbnail. */
  cover?: string | null;
}

export function emptyWhiteboardSnapshot(): WhiteboardSnapshot {
  return {
    version: 1,
    bg: "dark",
    grid: { enabled: true, size: 40, snap: false },
    strokes: [],
    shapes: [],
    texts: [],
    stickies: [],
    widgets: [],
    cover: null,
  };
}

const WHITEBOARD_BG = new Set<WhiteboardBg>(["light", "dark"]);
const STICKY_COLORS = new Set<StickyColorId>(["yellow", "pink", "blue", "green", "violet"]);

const CARD_ACCENTS = new Set<CardAccent>(["lime", "amber", "rose", "sky"]);

function isWhiteboardBg(v: unknown): v is WhiteboardBg {
  return typeof v === "string" && WHITEBOARD_BG.has(v as WhiteboardBg);
}

function parseStickies(raw: unknown): StickyNote[] {
  if (!Array.isArray(raw)) return [];
  const out: StickyNote[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const s = r as Partial<StickyNote>;
    if (typeof s.id !== "string" || typeof s.x !== "number" || typeof s.y !== "number") {
      continue;
    }
    out.push({
      id: s.id,
      x: s.x,
      y: s.y,
      w: typeof s.w === "number" && s.w > 20 ? s.w : 220,
      h: typeof s.h === "number" && s.h > 20 ? s.h : 180,
      value: typeof s.value === "string" ? s.value : "",
      color: STICKY_COLORS.has(s.color as StickyColorId) ? (s.color as StickyColorId) : "yellow",
      fontSize: typeof s.fontSize === "number" && s.fontSize > 6 ? s.fontSize : 26,
    });
  }
  return out;
}

function parseWidgets(raw: unknown): Widget[] {
  if (!Array.isArray(raw)) return [];
  const out: Widget[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const w = r as Record<string, unknown>;
    if (typeof w.id !== "string" || typeof w.x !== "number" || typeof w.y !== "number") {
      continue;
    }
    const width = typeof w.w === "number" && w.w > 20 ? (w.w as number) : 260;
    const height = typeof w.h === "number" && w.h > 20 ? (w.h as number) : 120;
    if (w.kind === "player") {
      if (typeof w.playerId !== "string") continue;
      out.push({
        kind: "player",
        id: w.id,
        x: w.x,
        y: w.y,
        w: width,
        h: typeof w.h === "number" && w.h > 20 ? (w.h as number) : 96,
        playerId: w.playerId,
        name: typeof w.name === "string" ? w.name : "",
        position: typeof w.position === "string" ? w.position : null,
        shirt: typeof w.shirt === "number" ? w.shirt : null,
      });
      continue;
    }
    if (w.kind === "card") {
      out.push({
        kind: "card",
        id: w.id,
        x: w.x,
        y: w.y,
        w: width,
        h: typeof w.h === "number" && w.h > 20 ? (w.h as number) : 180,
        title: typeof w.title === "string" ? w.title : "",
        body: typeof w.body === "string" ? w.body : "",
        accent: CARD_ACCENTS.has(w.accent as CardAccent) ? (w.accent as CardAccent) : "lime",
      });
    }
  }
  return out;
}

/**
 * Parse a persisted viewport `{scale, x, y}`. Returns `undefined` for invalid
 * or missing data so the editor falls back to its default pinch-zoom state.
 * `scale` is clamped to a sane range to defend against tampered snapshots.
 */
function parseViewport(raw: unknown): { scale: number; x: number; y: number } | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const v = raw as Partial<{ scale: number; x: number; y: number }>;
  if (
    typeof v.scale !== "number" ||
    typeof v.x !== "number" ||
    typeof v.y !== "number" ||
    !Number.isFinite(v.scale) ||
    !Number.isFinite(v.x) ||
    !Number.isFinite(v.y)
  ) {
    return undefined;
  }
  return {
    scale: Math.min(8, Math.max(0.1, v.scale)),
    x: v.x,
    y: v.y,
  };
}
export function parseWhiteboardSnapshot(json: string): WhiteboardSnapshot {
  try {
    const raw = JSON.parse(json) as Partial<WhiteboardSnapshot>;
    const base = emptyWhiteboardSnapshot();
    return {
      version: 1,
      bg: isWhiteboardBg(raw.bg) ? raw.bg : base.bg,
      grid: {
        enabled: typeof raw.grid?.enabled === "boolean" ? raw.grid.enabled : base.grid.enabled,
        size:
          typeof raw.grid?.size === "number" && raw.grid.size > 0 ? raw.grid.size : base.grid.size,
        snap: typeof raw.grid?.snap === "boolean" ? raw.grid.snap : base.grid.snap,
      },
      strokes: Array.isArray(raw.strokes) ? raw.strokes : [],
      shapes: Array.isArray(raw.shapes) ? raw.shapes : [],
      texts: Array.isArray(raw.texts) ? raw.texts : [],
      stickies: parseStickies(raw.stickies),
      widgets: parseWidgets((raw as { widgets?: unknown }).widgets),
      viewport: parseViewport((raw as { viewport?: unknown }).viewport),
      cover: typeof raw.cover === "string" && raw.cover.length > 0 ? raw.cover : null,
    };
  } catch {
    return emptyWhiteboardSnapshot();
  }
}

/** Visual presets for sticky cards. Keep contrast pleasant on both BGs. */
export const STICKY_PALETTE: Record<
  StickyColorId,
  { paper: string; stroke: string; ink: string; label: string }
> = {
  yellow: {
    paper: "#fde68a",
    stroke: "#b45309",
    ink: "#1c1300",
    label: "Gelb",
  },
  pink: { paper: "#fbcfe8", stroke: "#9d174d", ink: "#1c0014", label: "Pink" },
  blue: { paper: "#bfdbfe", stroke: "#1d4ed8", ink: "#001a3a", label: "Blau" },
  green: { paper: "#bbf7d0", stroke: "#15803d", ink: "#04200f", label: "Grün" },
  violet: {
    paper: "#ddd6fe",
    stroke: "#6d28d9",
    ink: "#15082f",
    label: "Violett",
  },
};

/** Visual presets for entity cards (drill / Übungsblöcke). */
export const CARD_PALETTE: Record<
  CardAccent,
  { paper: string; stroke: string; ink: string; subtle: string; label: string }
> = {
  lime: {
    paper: "#1a2615",
    stroke: "#97fc00",
    ink: "#eafbd0",
    subtle: "rgba(234,251,208,0.65)",
    label: "Lime",
  },
  amber: {
    paper: "#291d0d",
    stroke: "#f59e0b",
    ink: "#fde6c2",
    subtle: "rgba(253,230,194,0.65)",
    label: "Amber",
  },
  rose: {
    paper: "#2a1218",
    stroke: "#f43f5e",
    ink: "#ffd0d8",
    subtle: "rgba(255,208,216,0.65)",
    label: "Rosé",
  },
  sky: {
    paper: "#0e2030",
    stroke: "#38bdf8",
    ink: "#d0eaff",
    subtle: "rgba(208,234,255,0.65)",
    label: "Sky",
  },
};
